import {Link, type TokenProps} from '@primer/react'
import {Token} from '@primer/styled-react'
import type {BetterSystemStyleObject} from '@primer/styled-react'
import {useEffect, useRef, useState} from 'react'
import styles from './IssueTypeToken.module.css'
import {Tooltip} from '@primer/react/next'
import {clsx} from 'clsx'

export type IssueTypeTokenProps = {
  name: string
  color: string
  href: string
  getTooltipText: (isTextTruncated: boolean) => string | undefined
  sx?: BetterSystemStyleObject
  className?: string
} & Pick<TokenProps, 'size'>

export const IssueTypeToken = ({name, color, href, getTooltipText, size, sx, className}: IssueTypeTokenProps) => {
  const tokenRef = useRef<HTMLElement>(null)
  const [truncated, setTruncated] = useState(false)

  useEffect(() => setTruncated(isTextTruncated(tokenRef.current)), [setTruncated, tokenRef])

  const internal = (
    <Link href={href}>
      <Token
        className={clsx(styles.token, className)}
        data-color={color}
        ref={tokenRef}
        sx={sx}
        // the span in the text is allowing us to check if the text is truncated
        text={<span className={styles.tokenText}>{name}</span>}
      />
    </Link>
  )

  const tooltipText = getTooltipText(truncated)

  return (
    <div className={styles.issueTypeTokenContainer} data-size={size}>
      {tooltipText ? <Tooltip text={tooltipText}>{internal}</Tooltip> : internal}
    </div>
  )
}
// This is not bullet proof, since the true inner width of the element is rounded down.
// I think it's pretty good though. See discussion here https://stackoverflow.com/questions/64689074/how-to-check-if-text-is-truncated-by-css-using-javascript
const isTextTruncated = (e: HTMLElement | null): boolean => {
  if (!e || !e.firstElementChild) return false
  return e.firstElementChild.scrollWidth > e.firstElementChild.clientWidth
}
